/**
 * Bergep (Rental Equipment) API Client
 * Epic 13: Bérgép Management
 */

import type { RentalAccessoryCatalogItem, RentalAccessoryCatalogResponse } from '@kgc/api-contract';
import { api } from './client';

// ============================================
// TYPES
// ============================================

export type EquipmentStatus =
  | 'AVAILABLE'
  | 'RENTED'
  | 'MAINTENANCE'
  | 'RESERVED'
  | 'RETIRED'
  | 'DAMAGED'
  | 'LOST';

export type EquipmentCategory =
  | 'POWER_TOOL'
  | 'HAND_TOOL'
  | 'GARDEN'
  | 'CONSTRUCTION'
  | 'CLEANING'
  | 'GENERATOR'
  | 'COMPRESSOR'
  | 'WELDING'
  | 'OTHER';

export type EquipmentCondition = 'EXCELLENT' | 'GOOD' | 'FAIR' | 'POOR';

export interface BergepItem {
  id: string;
  serialNumber: string;
  inventoryCode?: string;
  name: string;
  description?: string;
  category: EquipmentCategory;
  condition: EquipmentCondition;
  status: EquipmentStatus;
  brand?: string;
  model?: string;
  dailyRate: number;
  weeklyRate: number;
  monthlyRate: number;
  hourlyRate?: number;
  depositAmount: number;
  purchaseDate?: string;
  purchasePrice?: number;
  warrantyExpiry?: string;
  maintenanceIntervalDays?: number;
  lastMaintenanceDate?: string;
  nextMaintenanceDate?: string;
  notes?: string;
  locationId: string;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
}

export interface BergepListResponse {
  data: BergepItem[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
  };
}

export interface BergepResponse {
  data: BergepItem;
}

export interface BergepFilters {
  status?: EquipmentStatus | undefined;
  category?: EquipmentCategory | undefined;
  condition?: EquipmentCondition | undefined;
  brand?: string | undefined;
  search?: string | undefined;
  availableOnly?: boolean | undefined;
  maintenanceDueSoon?: boolean | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

// ============================================
// HELPERS
// ============================================

/**
 * Build query params for bergep controller.
 * Auth is now handled via JWT (Authorization header + X-Tenant-ID) by the api client.
 */
function getAuthParams(): URLSearchParams {
  return new URLSearchParams();
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get list of rental equipment with filters
 */
export async function getBergepList(filters: BergepFilters = {}): Promise<BergepListResponse> {
  const params = getAuthParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.category) params.append('category', filters.category);
  if (filters.condition) params.append('condition', filters.condition);
  if (filters.brand) params.append('brand', filters.brand);
  if (filters.search) params.append('search', filters.search);
  if (filters.availableOnly) params.append('availableOnly', 'true');
  if (filters.maintenanceDueSoon) params.append('maintenanceDueSoon', 'true');
  // M-018 fix: Use !== undefined to avoid falsy-zero bug
  if (filters.page !== undefined) params.append('page', filters.page.toString());
  if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize.toString());

  const queryString = params.toString();
  // Backend returns { equipment: [...], total, page, pageSize, hasMore }
  const response = await api.get<{
    equipment: BergepItem[];
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  }>(`/rental-equipment?${queryString}`);

  return {
    data: response.equipment ?? [],
    meta: {
      total: response.total ?? 0,
      page: response.page ?? 1,
      pageSize: response.pageSize ?? 20,
    },
  };
}

/**
 * Get single equipment by ID
 */
export async function getBergepById(id: string): Promise<BergepResponse> {
  const params = getAuthParams();
  // Backend returns raw equipment object, wrap in { data: ... }
  const response = await api.get<BergepItem>(`/rental-equipment/${id}?${params.toString()}`);
  return { data: response };
}

/**
 * Get equipment rental history
 */
export interface RentalHistoryItem {
  rentalId: string;
  rentalCode: string;
  partnerId: string;
  partnerName: string;
  startDate: string;
  expectedEnd: string;
  actualEnd: string | null;
  issuedByName: string | null;
  returnedByName: string | null;
  itemTotal: number;
  status: string;
}

export interface RentalHistoryResponse {
  equipmentId: string;
  totalRentals: number;
  lastRenterName: string | null;
  worksheetCount: number;
  rentals: RentalHistoryItem[];
  page: number;
  pageSize: number;
  totalPages: number;
}

export async function getBergepRentalHistory(
  id: string,
  page = 1,
  pageSize = 20
): Promise<RentalHistoryResponse> {
  const params = getAuthParams();
  params.append('page', page.toString());
  params.append('pageSize', pageSize.toString());
  return api.get<RentalHistoryResponse>(
    `/rental-equipment/${id}/rental-history?${params.toString()}`
  );
}

// ============================================
// ACCESSORIES (Story 57-2)
// ============================================

export interface BergepAccessory {
  id: string;
  name: string;
  description?: string;
  isRequired: boolean;
  value?: number;
}

export interface BergepAccessoriesResponse {
  accessories: BergepAccessory[];
}

/**
 * Get accessories for a specific equipment item
 */
export async function fetchEquipmentAccessories(id: string): Promise<BergepAccessoriesResponse> {
  const params = getAuthParams();
  const queryString = params.toString();
  const url = queryString
    ? `/rental-equipment/${id}/accessories?${queryString}`
    : `/rental-equipment/${id}/accessories`;
  return api.get<BergepAccessoriesResponse>(url);
}

// ============================================
// E-105 105-8b: Wizard scan-by-code + suggested-accessories
// ============================================

export interface WizardScannedEquipment {
  id: string;
  equipmentCode: string;
  serialNumber: string;
  qrCode: string | null;
  factoryBarcode: string | null;
  status: string;
  productId: string;
  productName: string;
  productBrand: string | null;
  productModel: string | null;
  dailyRate: number | null;
  weeklyRate: number | null;
  monthlyRate: number | null;
  depositAmount: number | null;
  matchedField: 'qrCode' | 'factoryBarcode' | 'equipmentCode' | 'serialNumber';
}

/**
 * Auto-detect scanner endpoint — egy code mező 4 lehetséges scan field-en match-el
 * (qrCode → factoryBarcode → equipmentCode → serialNumber).
 */
export async function scanEquipmentByCode(code: string): Promise<WizardScannedEquipment> {
  return api.post<WizardScannedEquipment>('/rental-equipment/scan-by-code', { code });
}

export interface SuggestedAccessoryItem {
  itemId: string;
  itemType: 'PRODUCT' | 'EQUIPMENT';
  productId: string | null;
  rentalEquipmentId: string | null;
  name: string;
  quantity: number;
  unitPrice: number | null;
  isOptional: boolean;
  // Q-T042 (#576 FE-tükör): a kanonikus PackageItemSettlement enum (ADR-085) RENTAL-t ad,
  // a korábbi 'EXTRA' type-lie volt (a BE már RENTAL-t küld) → FE-típus sync a valósághoz.
  settlement: 'INCLUDED' | 'RENTAL' | 'CONSIGNMENT';
  sortOrder: number;
}

export interface SuggestedAccessoriesResponse {
  equipmentId: string;
  packages: Array<{
    packageId: string;
    packageCode: string;
    packageName: string;
    items: SuggestedAccessoryItem[];
  }>;
}

/**
 * Get suggested accessories for an equipment via the Package system (ADR-084).
 */
export async function fetchSuggestedAccessories(
  equipmentId: string
): Promise<SuggestedAccessoriesResponse> {
  return api.get<SuggestedAccessoriesResponse>(
    `/rental-equipment/${equipmentId}/suggested-accessories`
  );
}

// ============================================
// E-105 105-8b: New backend endpoints (EquipmentAccessoryController)
// Endpoint prefix: /rental/equipment (NEM /rental-equipment)
// ============================================

export interface AccessoryDto {
  id: string;
  equipmentId: string;
  name: string;
  description: string | undefined;
  serialNumber: string | undefined;
  condition: string;
  isRequired: boolean;
  value: number | undefined;
  createdAt: string;
}

export interface SuggestedAccessoryDto extends AccessoryDto {
  rank: number;
  usageCount: number;
}

export interface AccessoryListResponse105 {
  data: AccessoryDto[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
  };
}

export interface SuggestedAccessoriesResponse105 {
  data: SuggestedAccessoryDto[];
  meta: {
    // D2 rename: 'history' was misleading (same sort as 'default'). 'required_sort' is the honest
    // proxy label until frequency junction exists ('frequency' arrives with 105-8b-v2).
    source: 'required_sort' | 'default' | 'frequency';
    equipmentId: string;
    limit: number;
  };
}

/**
 * 105-8b: Fetch paginated accessory list with optional search + requiredOnly filter.
 * Uses new EquipmentAccessoryController (GET /rental/equipment/:id/accessories).
 */
export async function fetchAccessoriesPaginated(
  equipmentId: string,
  opts: { search?: string; requiredOnly?: boolean; page?: number; pageSize?: number } = {},
  signal?: AbortSignal
): Promise<AccessoryListResponse105> {
  const params = new URLSearchParams();
  if (opts.search) params.set('search', opts.search);
  if (opts.requiredOnly) params.set('requiredOnly', 'true');
  if (opts.page !== undefined) params.set('page', String(opts.page));
  if (opts.pageSize !== undefined) params.set('pageSize', String(opts.pageSize));
  const qs = params.toString();
  return api.get<AccessoryListResponse105>(
    `/rental/equipment/${equipmentId}/accessories${qs ? `?${qs}` : ''}`,
    signal ? { signal } : undefined
  );
}

/**
 * 105-8b: Fetch top-N suggested accessories for an equipment (AC-3).
 * Source: 'required_sort' | 'default' | 'frequency' (see D2 rename).
 * Uses new EquipmentAccessoryController (GET /rental/equipment/:id/suggested-accessories).
 */
export async function fetchFrequencySuggestedAccessories(
  equipmentId: string,
  limit = 8,
  signal?: AbortSignal
): Promise<SuggestedAccessoriesResponse105> {
  return api.get<SuggestedAccessoriesResponse105>(
    `/rental/equipment/${equipmentId}/suggested-accessories?limit=${limit}`,
    signal ? { signal } : undefined
  );
}

// ============================================
// ACCESSORY CATALOG — kontraktus a @kgc/api-contract-ból (E-105 BE-1 #269, 2026-06-05).
// A FE-local sync-pointer típus megszűnt; re-export a meglevő api-client-konzumenseknek (hook, spec).
// endpoint: GET /rental-equipment/:id/accessory-catalog
// ============================================

export type { RentalAccessoryCatalogItem, RentalAccessoryCatalogResponse };

/**
 * Fetch the accessory catalog for an equipment (day-1-unblock slice).
 * Ratified contract: Discord 2026-06-05 11:48 (Zeno).
 * GET /rental-equipment/:id/accessory-catalog
 */
export async function fetchAccessoryCatalog(
  equipmentId: string,
  signal?: AbortSignal
): Promise<RentalAccessoryCatalogResponse> {
  return api.get<RentalAccessoryCatalogResponse>(
    `/rental-equipment/${equipmentId}/accessory-catalog`,
    signal ? { signal } : undefined
  );
}
